import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/hen-ui/utils';

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentProps<typeof TabsPrimitive.Root>
>(({ className, orientation, ...props }, ref) => (
  <TabsPrimitive.Root
    data-slot='tabs'
    orientation={orientation}
    className={cn(
      'group/tabs flex w-full min-w-0 max-w-full gap-6 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:items-center',
      className,
    )}
    ref={ref}
    {...props}
  />
));

Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentProps<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    data-slot='tabs-list'
    ref={ref}
    className={cn(
      // Base layout
      'inline-flex items-center w-fit max-w-full',
      // Horizontal scroll with hidden scrollbar
      'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
      // Spacing
      'gap-lg',
      // Orientation-specific
      'group-data-[orientation=horizontal]/tabs:h-8',
      'group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col',
      className,
    )}
    {...props}
  />
));

TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentProps<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    data-slot='tabs-trigger'
    ref={ref}
    className={cn(
      // Base layout
      'relative inline-flex flex-1 items-center justify-center whitespace-nowrap',
      // Spacing
      'gap-1 px-1.5',
      // Typography
      'text-sm font-bold',
      // Text Colors
      'text-neutral-900',
      // Transitions
      'transition-all',
      // Focus state
      'focus-visible:outline-neutral-200 focus-visible:outline-1 focus-visible:ring focus-visible:ring-neutral-100 focus-visible:rounded-md',
      // Disabled state
      'disabled:pointer-events-none disabled:opacity-50',

      // Active indicator (after pseudo-element)
      'after:bg-neutral-900 md:hover:after:bg-neutral-100 after:absolute after:opacity-0 after:transition-opacity',
      // Show indicator on hover (lighter) and active (full)
      'md:hover:after:opacity-100',
      // Horizontal orientation indicator
      'group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:-bottom-1 group-data-[orientation=horizontal]/tabs:after:h-0.5',
      // Vertical orientation indicator
      'group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5',
      // Show indicator when active
      'data-[state=active]:after:opacity-100',

      // Vertical orientation adjustments
      'group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start',

      // Icon styling
      '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
      '[&_[data-hen-icon]]:pointer-events-none [&_[data-hen-icon]]:shrink-0 [&_[data-hen-icon]:not([class*="size-"])]:size-4',

      className,
    )}
    {...props}
  />
));

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentProps<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    data-slot='tabs-content'
    ref={ref}
    className={cn(
      'flex-1 text-sm outline-none',
      'transition-opacity duration-slow ease-out',
      'data-[state=inactive]:opacity-0 data-[state=inactive]:pointer-events-none',
      'data-[state=active]:opacity-100',
      // Horizontal tabs: parent uses items-center so panels must explicitly span
      // the content width; otherwise wide children (e.g. carousels) expand the page.
      'group-data-[orientation=horizontal]/tabs:w-full group-data-[orientation=horizontal]/tabs:min-w-0 group-data-[orientation=horizontal]/tabs:max-w-full group-data-[orientation=horizontal]/tabs:self-stretch',
      className,
    )}
    {...props}
  />
));

TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
